import { Hash, Users, DoorOpen, Layers } from "lucide-react";

function Chip({ icon: Icon, label, value, testid }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700" data-testid={testid}>
      <Icon className="h-3.5 w-3.5 text-slate-400" />
      {label && <span className="text-slate-400">{label}</span>}
      <span className="font-medium">{value}</span>
    </span>
  );
}

export function StudentHeader({ school, student, section }) {
  const initials = (student.full_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const periods = (student.schedule || []).filter((c) => !c.homeroom);
  const events = student.period_events || [];
  return (
    <div className="flex flex-wrap items-start gap-5" data-testid="parent-student-header">
      {student.photo_data_url ? (
        <img src={student.photo_data_url} alt="" className="h-20 w-20 shrink-0 rounded-2xl object-cover ring-4 ring-white shadow-md" data-testid="parent-student-photo" />
      ) : (
        <div className="h-20 w-20 shrink-0 rounded-2xl bg-primary text-white flex items-center justify-center font-display text-2xl font-bold ring-4 ring-white shadow-md" data-testid="parent-student-photo">{initials}</div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 truncate" data-testid="parent-student-school">{school.name}</div>
          {section && <span className="hidden md:inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest text-primary" data-testid="parent-student-section">{section}</span>}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 leading-tight" data-testid="parent-student-name">{student.full_name}</h1>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Chip icon={Hash} label="ID" value={student.student_number} testid="parent-student-number" />
          <Chip icon={Layers} label="Grade" value={student.grade} testid="parent-student-grade" />
          <Chip icon={DoorOpen} value={[student.class_name, student.room && `Rm ${student.room}`].filter(Boolean).join(" · ")} testid="parent-student-class" />
          <Chip icon={Users} label="Teacher" value={student.teacher_name} testid="parent-student-teacher" />
          {!student.grade && !student.class_name && <span className="text-xs text-slate-500">No class assigned yet</span>}
        </div>
        {periods.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="parent-student-schedule">
            {periods.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-[11px] text-slate-700" data-testid={`parent-schedule-${c.id}`}>
                <b className="text-primary">{c.period || "Class"}</b> {c.name}{c.teacher_name ? ` · ${c.teacher_name}` : ""}{c.room ? ` · Rm ${c.room}` : ""}
              </span>
            ))}
          </div>
        )}
      </div>
      {events.length > 0 && (
        <div className="w-full lg:w-80 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="parent-period-events">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Today's classes</div>
          <div className="space-y-1">
            {[...events].reverse().map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-xs" data-testid={`parent-period-event-${e.id}`}>
                <span className={`w-9 shrink-0 rounded px-1 py-0.5 text-center font-semibold ${e.event_type === "period_in" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>{e.event_type === "period_in" ? "IN" : "OUT"}</span>
                <span className="font-medium text-slate-900 truncate">{e.period} · {e.class_name}</span>
                {e.late && <span className="rounded bg-amber-100 px-1 text-amber-800 font-semibold">Late</span>}
                <span className="ml-auto shrink-0 text-slate-500">{new Date(e.occurred_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
